import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useSpotifyCanvasConnect } from '../../../servicesPanel/spotifyCanvas/useSpotifyCanvasConnect';
import { ProfileUpgradeButton } from '../page/ProfileUpgradeButton';
import { ProfileDownloadLink } from '../page/ProfileDownloadLink';
import { spotifyUploadMessages as s } from './spotifyUploadMessages';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SpotifySheetStateProps {
  kind: 'manual' | 'free' | 'notConnected' | 'reconnect' | 'nothing';
  accountId: number;
  artist: string;
  /** The downloads of the steps (no Auto upload access). */
  downloads: ReadonlyArray<{ key: string; href: string }>;
  onConnected: () => void;
}

/**
 * The sheet before Send when nothing can go yet (design c2.3, c2.4, c2.7, c2.8): Connect or "Sign
 * in again" (the Spotify for Artists sign-in of the Canvas row), Upgrade on Free, the steps with no
 * Auto upload access, or "Make it first".
 */
const SpotifySheetStateComponent: React.FC<SpotifySheetStateProps> = ({
  kind,
  accountId,
  artist,
  downloads,
  onConnected,
}) => {
  const intl = useIntl();
  const { connect, isConnecting, error } = useSpotifyCanvasConnect({ accountId, onConnected });

  if (kind === 'manual') {
    return (
      <div className={styles.howTo} data-testid="spotify-sheet-manual">
        <b>{intl.formatMessage(s.manualTitle)}</b>
        <ol>
          <li>{intl.formatMessage(s.manual1)}</li>
          <li>{intl.formatMessage(s.manual2)}</li>
          <li>{intl.formatMessage(s.manual3)}</li>
        </ol>
        <div className={styles.row}>
          {downloads.map((one) => (
            <ProfileDownloadLink key={one.key} href={one.href} testId={`spotify-sheet-download-${one.key}`} />
          ))}
        </div>
      </div>
    );
  }
  if (kind === 'free') {
    return (
      <div className="d-flex flex-column gap-2" data-testid="spotify-sheet-free">
        <b>{intl.formatMessage(s.freeTitle)}</b>
        <span className={styles.meta}>{withBrandWord(intl.formatMessage(s.freeBody))}</span>
        <div>
          <ProfileUpgradeButton small />
        </div>
      </div>
    );
  }
  if (kind === 'nothing') {
    return (
      <span className={styles.meta} data-testid="spotify-sheet-nothing">
        {intl.formatMessage(s.nothing)}
      </span>
    );
  }
  const isReconnect = kind === 'reconnect';
  return (
    <div className="d-flex flex-column gap-2" data-testid={`spotify-sheet-${kind}`}>
      <b>{intl.formatMessage(isReconnect ? s.reconnectTitle : s.notConnectedTitle)}</b>
      {isReconnect ? (
        <div className={styles.error}>{intl.formatMessage(s.reconnectBody)}</div>
      ) : (
        <span className={styles.meta}>{intl.formatMessage(s.notConnectedBody, { artist })}</span>
      )}
      <div>
        <SMLButton
          variant={isReconnect ? 'default' : 'primary'}
          className={styles.sm}
          disabled={isConnecting}
          onClick={connect}
        >
          {intl.formatMessage(isReconnect ? s.signInAgain : s.connect)}
        </SMLButton>
      </div>
      {error !== undefined && <div className={styles.error}>{intl.formatMessage(s.generic)}</div>}
    </div>
  );
};

export const SpotifySheetState = TypedMemo(SpotifySheetStateComponent);
